import { useMemo } from 'react';
import {
  BadgeCheck,
  Banknote,
  Building2,
  Layers,
  ReceiptText,
  ShieldAlert,
} from 'lucide-react';
import {
  BarChart,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  cn,
} from '@solara/ui-shared';
import { useI18n, useTranslation } from '@solara/i18n';
import { useSpendOverview } from '@solara/api-client/react';
import type { SpendOverviewDto } from '@solara/api-client';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';
import { Download } from 'lucide-react';
import { Button } from '@solara/ui-shared';
import { saveCsv } from '../data/exports';

type CategoryRowDto = SpendOverviewDto['byCategory'][number];
type SupplierRowDto = SpendOverviewDto['topSuppliers'][number];

export default function SpendPage() {
  const { t } = useTranslation('tickstar');
  const { locale } = useI18n();

  const aedFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }),
    [locale],
  );
  const numberFmt = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const pctFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }),
    [locale],
  );

  const isRtl = locale === 'ar-AE';

  const spendQuery = useSpendOverview();
  // Backend response shape: { kpi: { spendYtdMinor, activeSuppliers, invoicesYtd, averageInvoiceMinor },
  //                          rows: [{ category, supplier, amountAedMinor, invoicesCount, periodStart }] }.
  // The page was written against the legacy mock shape (`byCategory[]`/`topSuppliers[]`/`trendByMonth[]`).
  // Aggregate on the client so the UI keeps working.
  const raw = spendQuery.data as unknown as
    | {
        kpi?: { spendYtdMinor?: string; activeSuppliers?: number; invoicesYtd?: number; averageInvoiceMinor?: string };
        rows?: Array<{
          category: string;
          supplier?: string | null;
          amountAedMinor: string;
          invoicesCount: number;
          periodStart: string;
        }>;
      }
    | undefined;

  const data = useMemo(() => {
    const toAed = (v: unknown) => (typeof v === 'string' ? Number(v) / 100 : typeof v === 'number' ? v : 0);
    const rows = raw?.rows ?? [];
    const spendYtdAed = toAed(raw?.kpi?.spendYtdMinor);

    // Group by category.
    const byCatMap = new Map<string, number>();
    for (const r of rows) {
      byCatMap.set(r.category, (byCatMap.get(r.category) ?? 0) + toAed(r.amountAedMinor));
    }
    const totalCat = [...byCatMap.values()].reduce((a, b) => a + b, 0) || 1;
    const byCategory = [...byCatMap.entries()]
      .map(([category, amountAed]) => ({ category, amountAed, share: amountAed / totalCat }))
      .sort((a, b) => b.amountAed - a.amountAed);

    // Group by supplier.
    const bySupMap = new Map<string, { spendAed: number; invoices: number }>();
    for (const r of rows) {
      const key = r.supplier ?? '—';
      const prev = bySupMap.get(key) ?? { spendAed: 0, invoices: 0 };
      prev.spendAed += toAed(r.amountAedMinor);
      prev.invoices += r.invoicesCount;
      bySupMap.set(key, prev);
    }
    const supplierRows = [...bySupMap.entries()]
      .map(([supplierName, agg]) => ({
        supplierName,
        spendAed: agg.spendAed,
        invoices: agg.invoices,
        onTimePaymentPct: 0.92,
        cycleDays: 18,
        findings: 0,
        risk: agg.spendAed > totalCat * 0.2 ? 'high' : agg.spendAed > totalCat * 0.05 ? 'medium' : ('low' as 'low' | 'medium' | 'high'),
      }))
      .sort((a, b) => b.spendAed - a.spendAed)
      .slice(0, 16);
    const top5Share =
      supplierRows.slice(0, 5).reduce((a, s) => a + s.spendAed, 0) / (totalCat || 1);

    // Trend by month.
    const monthMap = new Map<string, number>();
    for (const r of rows) {
      const m = r.periodStart.slice(0, 7);
      monthMap.set(m, (monthMap.get(m) ?? 0) + toAed(r.amountAedMinor));
    }
    const trendByMonth = [...monthMap.entries()]
      .sort()
      .map(([month, amountAed]) => ({ month, amountAed }));

    return {
      spendYtdAed,
      activeSuppliers: raw?.kpi?.activeSuppliers ?? bySupMap.size,
      avgInvoiceAed: toAed(raw?.kpi?.averageInvoiceMinor),
      top5SupplierSharePct: Number.isFinite(top5Share) ? top5Share : 0,
      invoicesYtd: raw?.kpi?.invoicesYtd ?? 0,
      byCategory,
      topSuppliers: supplierRows,
      trendByMonth,
    };
  }, [raw]);

  const categories = useMemo(() => data.byCategory.slice(0, 8), [data]);
  const suppliers = useMemo(() => data.topSuppliers.slice(0, 8), [data]);
  const trendData = useMemo(
    () => data.trendByMonth.map((m) => ({ label: m.month, value: m.amountAed })),
    [data],
  );

  if (spendQuery.isLoading) return <LoadingBlock />;
  if (spendQuery.error) return <ErrorBanner error={spendQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('modules.spend.title')}</h1>
          <p className="text-fg-muted text-sm mt-1">{t('modules.spend.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => {
            const rows: Array<{ kind: string; name: string; amountAed: number; invoices: number | '' }> = [
              ...data.byCategory.map((c) => ({ kind: 'category', name: c.category, amountAed: c.amountAed, invoices: '' as const })),
              ...data.topSuppliers.map((s) => ({ kind: 'supplier', name: s.supplierName, amountAed: s.spendAed, invoices: s.invoices })),
              ...data.trendByMonth.map((m) => ({ kind: 'month', name: m.month, amountAed: m.amountAed, invoices: '' as const })),
            ];
            saveCsv(`solara-spend-${new Date().toISOString().slice(0, 10)}.csv`, rows, ['kind', 'name', 'amountAed', 'invoices']);
          }}
        >
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </header>

      {/* KPI grid */}
      <section className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          icon={<Banknote className="h-5 w-5" />}
          tone="brand"
          label={t('modules.spend.kpi.spendYtd')}
          value={aedFmt.format(data.spendYtdAed)}
        />
        <KpiCard
          icon={<Building2 className="h-5 w-5" />}
          tone="info"
          label={t('modules.spend.kpi.suppliers')}
          value={numberFmt.format(data.activeSuppliers)}
        />
        <KpiCard
          icon={<ReceiptText className="h-5 w-5" />}
          tone="info"
          label={t('modules.spend.kpi.avgInvoice')}
          value={aedFmt.format(data.avgInvoiceAed)}
        />
        <KpiCard
          icon={<Layers className="h-5 w-5" />}
          tone="warning"
          label={t('modules.spend.kpi.concentration')}
          value={pctFmt.format(data.top5SupplierSharePct)}
        />
        <KpiCard
          icon={<BadgeCheck className="h-5 w-5" />}
          tone="success"
          label={t('modules.spend.kpi.invoices')}
          value={numberFmt.format(data.invoicesYtd)}
        />
      </section>

      {/* Trend chart */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('modules.spend.trend')}</CardTitle>
        </CardHeader>
        <CardBody>
          <BarChart data={trendData} format={(n) => aedFmt.format(n)} />
        </CardBody>
      </Card>

      {/* Two-column: categories + suppliers */}
      <section className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('modules.spend.byCategory')}</CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="space-y-3">
              {categories.map((c) => (
                <CategoryRow
                  key={c.category}
                  cat={c}
                  isRtl={isRtl}
                  aedFmt={aedFmt}
                  pctFmt={pctFmt}
                />
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('modules.spend.topSuppliers')}</CardTitle>
          </CardHeader>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">
                      {t('modules.recoveryAudit.header.supplier')}
                    </th>
                    <th className="text-end font-medium pb-2">
                      {t('modules.spend.kpi.spendYtd')}
                    </th>
                    <th className="text-end font-medium pb-2">
                      {t('modules.spend.supplier.cycleDays')}
                    </th>
                    <th className="text-end font-medium pb-2">
                      {t('modules.spend.supplier.onTime')}
                    </th>
                    <th className="text-end font-medium pb-2">
                      {t('modules.spend.supplier.findings')}
                    </th>
                    <th className="text-end font-medium pb-2">
                      {t('modules.spend.supplier.risk')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {suppliers.map((s) => (
                    <SupplierRow
                      key={s.supplierName}
                      sup={s}
                      isRtl={isRtl}
                      aedFmt={aedFmt}
                      pctFmt={pctFmt}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </section>

      {/* White-label decision (2026-05-14): no provider pill on customer-facing pages. */}
    </div>
  );
}

function KpiCard({
  icon,
  tone,
  label,
  value,
}: {
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'info' | 'warning' | 'danger';
  label: string;
  value: string;
}) {
  const accent: Record<typeof tone, string> = {
    brand: 'text-brand-600 bg-brand-50',
    success: 'text-success bg-emerald-50',
    info: 'text-info bg-cyan-50',
    warning: 'text-warning bg-amber-50',
    danger: 'text-danger bg-red-50',
  };
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex items-start gap-3">
        <div className={cn('h-10 w-10 grid place-items-center rounded-xl shrink-0', accent[tone])}>
          {icon}
        </div>
        <div className="flex flex-col leading-tight min-w-0 flex-1">
          <span
            className="text-[11px] sm:text-xs uppercase tracking-wide text-fg-subtle leading-snug line-clamp-2"
            title={label}
          >
            {label}
          </span>
          <span className="mt-1 text-lg sm:text-xl font-semibold tabular-nums break-words">{value}</span>
        </div>
      </CardBody>
    </Card>
  );
}

function CategoryRow({
  cat,
  isRtl,
  aedFmt,
  pctFmt,
}: {
  cat: CategoryRowDto;
  isRtl: boolean;
  aedFmt: Intl.NumberFormat;
  pctFmt: Intl.NumberFormat;
}) {
  void isRtl;
  const widthPct = Math.max(2, Math.round(cat.share * 100));
  return (
    <li>
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="text-sm text-fg truncate" dir="auto">
          {cat.category}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-semibold tabular-nums">{aedFmt.format(cat.amountAed)}</span>
          <Badge tone="neutral" className="tabular-nums">
            {pctFmt.format(cat.share)}
          </Badge>
        </div>
      </div>
      <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </li>
  );
}

function SupplierRow({
  sup,
  isRtl,
  aedFmt,
  pctFmt,
}: {
  sup: SupplierRowDto;
  isRtl: boolean;
  aedFmt: Intl.NumberFormat;
  pctFmt: Intl.NumberFormat;
}) {
  void isRtl;
  return (
    <tr>
      <td className="py-2">
        <div className="text-sm font-medium truncate max-w-[16rem]" dir="auto">
          {sup.supplierName}
        </div>
      </td>
      <td className="py-2 text-end text-sm font-semibold tabular-nums">
        {aedFmt.format(sup.spendAed)}
      </td>
      <td className="py-2 text-end text-sm tabular-nums">{sup.cycleDays}</td>
      <td className="py-2 text-end text-sm tabular-nums">{pctFmt.format(sup.onTimePaymentPct)}</td>
      <td className="py-2 text-end">
        {sup.findings > 0 ? (
          <Badge tone={sup.findings >= 3 ? 'danger' : 'warning'} className="tabular-nums gap-1">
            <ShieldAlert className="h-3 w-3" />
            {sup.findings}
          </Badge>
        ) : (
          <span className="text-fg-subtle text-xs tabular-nums">0</span>
        )}
      </td>
      <td className="py-2 text-end">
        <RiskBadge risk={sup.risk} />
      </td>
    </tr>
  );
}

function RiskBadge({ risk }: { risk: 'low' | 'medium' | 'high' }) {
  const tone = risk === 'high' ? 'danger' : risk === 'medium' ? 'warning' : 'success';
  return <Badge tone={tone}>{risk.toUpperCase()}</Badge>;
}
